/**
 * SistemicMarquee — cinta CTA de marca Sistemic.
 *
 * Estandariza el "cinturón" inferior con la identidad oficial de
 * Sistemic (azul #007dd7, Space Grotesk, NodeMark, glow). Reemplaza la
 * versión ad-hoc de SistemicOutro con tokens scoped.
 *
 * AISLAMIENTO: todos los estilos cuelgan de .sistemic-scope vía CSS
 * vars inline en el wrapper. NO tocan el theming global de iftmx
 * (que es rojo). Es una "isla de marca" — los tokens viven aquí.
 *
 * Sin dependencias nuevas. Animación CSS pura. Mobile-first.
 * Respeta prefers-reduced-motion.
 *
 * Uso:
 *   import { SistemicMarquee } from '@/components/sistemic/SistemicMarquee';
 *   <SistemicMarquee />            // navega a sistemic.vercel.app
 *   <SistemicMarquee to="/acerca" />  // o ruta interna
 */

import { Link } from 'react-router-dom';
import { NodeMark } from './PoweredBySistemic';

const CHUNKS = [
  <>Hecho por <strong style={{ fontWeight: 700 }}>Sistemic</strong></>,
  <>¿Quieres una app así?</>,
  <>Apps · IA · Automatización</>,
  <>Para PyMEs · México</>,
];

interface Props {
  /** Ruta interna (Link) o se omite para ir a la web pública. */
  to?: string;
}

export function SistemicMarquee({ to }: Props) {
  const inner = (
    <div
      className="sistemic-scope relative block border-y overflow-hidden group"
      style={
        {
          // ----- Tokens de marca Sistemic (scoped, no globales) -----
          '--sis-accent': '#007dd7',
          '--sis-accent-soft': '#007dd773',
          '--sis-accent-ghost': '#007dd714',
          '--sis-border': '#007dd759',
          '--sis-glow': '0 0 22px #007dd780',
          fontFamily: "'Space Grotesk', sans-serif",
          borderColor: 'var(--sis-border)',
          background: `linear-gradient(90deg,
              var(--sis-accent-ghost) 0%,
              #007dd708 50%,
              var(--sis-accent-ghost) 100%
            ), hsl(var(--background))`,
        } as React.CSSProperties
      }
    >
      {/* Grid sutil de marca detrás del contenido */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          backgroundImage: `linear-gradient(#007dd70d 1px, transparent 1px),
            linear-gradient(90deg, #007dd70d 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
        }}
      />

      <div className="relative z-10 flex items-center gap-3 py-3 md:py-4 px-3 md:px-4">
        {/* NodeMark con glow */}
        <div
          className="shrink-0"
          style={{ filter: 'drop-shadow(0 0 8px #007dd773)' }}
        >
          <NodeMark size={18} />
        </div>

        {/* Marquee con fade en bordes vía mask (no divs encima) */}
        <div
          className="relative flex-1 overflow-hidden"
          style={{
            WebkitMaskImage:
              'linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)',
            maskImage:
              'linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)',
          }}
        >
          <div
            className="sistemic-mq whitespace-nowrap flex items-center text-[11px] md:text-xs font-medium"
            style={{ letterSpacing: '0.08em', color: 'var(--sis-accent)' }}
          >
            {Array.from({ length: 4 }).flatMap((_, rep) =>
              CHUNKS.map((chunk, i) => (
                <span key={`${rep}-${i}`} className="inline-flex items-center">
                  {chunk}
                  <span style={{ opacity: 0.4, margin: '0 18px' }}>◇</span>
                </span>
              )),
            )}
          </div>
        </div>

        {/* CTA circular */}
        <span
          className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full transition-transform group-hover:translate-x-1"
          style={{
            background: 'var(--sis-accent)',
            boxShadow: 'var(--sis-glow)',
            color: 'white',
          }}
        >
          <span className="text-sm font-black">→</span>
        </span>
      </div>

      {/* Animación scoped. prefers-reduced-motion la detiene. */}
      <style>{`
        .sistemic-scope .sistemic-mq {
          animation: sistemic-mq 28s linear infinite;
          will-change: transform;
        }
        .sistemic-scope:hover .sistemic-mq { animation-play-state: paused; }
        @keyframes sistemic-mq {
          from { transform: translateX(0); }
          to   { transform: translateX(-25%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .sistemic-scope .sistemic-mq { animation: none; }
        }
      `}</style>
    </div>
  );

  if (to) {
    return (
      <Link to={to} className="block mt-12" aria-label="Conoce Sistemic">
        {inner}
      </Link>
    );
  }
  return (
    <a
      href="https://sistemic.vercel.app"
      target="_blank"
      rel="noopener noreferrer"
      className="block mt-12"
      aria-label="Conoce Sistemic (abre en nueva pestaña)"
    >
      {inner}
    </a>
  );
}
